import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { useShowReturns } from '../../hooks/useShowReturns.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useAppSelector } from '../../hooks/useReactReduxHooks.ts';

import Shipping from 'dibs-icons/exports/Shipping.ts';

import { type PdpSharedShippingAndReturns_item$key } from './__generated__/PdpSharedShippingAndReturns_item.graphql.ts';

import dibsCss from 'dibs-css';

const shippingCopy = (
    <FormattedMessage defaultMessage="Shipping" id="PdpSharedShippingAndReturns.shipping" />
);
const shippingAndReturnsCopy = (
    <FormattedMessage
        defaultMessage="Shipping & Returns"
        id="PdpSharedShippingAndReturns.shippingAndReturns"
    />
);
const shippingShippingAndReturnsMobileCopy = (
    <FormattedMessage
        defaultMessage="Shipping and Returns"
        id="PdpSharedShippingAndReturns.shippingShippingAndReturnsMobile"
    />
);

const itemFragment = graphql`
    fragment PdpSharedShippingAndReturns_item on Item {
        ...useShowReturns_item
    }
`;

export const PdpSharedShippingAndReturns: FC<{
    item?: PdpSharedShippingAndReturns_item$key | null | undefined;
    children: ReactNode;
}> = ({ children, item: itemRef }) => {
    const { isMobile } = useServerVarsContext();
    const item = useFragment(itemFragment, itemRef);
    const showReturns = useShowReturns({ item });
    const { isPDPDesktopAggregationVariant } = useAppSelector(
        relay => relay?.relayVariables?.variables || {}
    );

    let title = shippingCopy;
    if (showReturns) {
        title = isMobile ? shippingShippingAndReturnsMobileCopy : shippingAndReturnsCopy;
    }

    return (
        <div
            className={classnames(dibsCss.wFull, dibsCss.roundedXxsmall, {
                [dibsCss.pSmaller]: !isMobile,
                [dibsCss.pxSmaller]: isMobile,
                [dibsCss.pyXsmall]: isMobile,
                [dibsCss.borderDolphin]: !isPDPDesktopAggregationVariant,
                [dibsCss.border]: !isPDPDesktopAggregationVariant,
                [dibsCss.borderSolid]: !isPDPDesktopAggregationVariant,
            })}
        >
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.textNoir,
                    dibsCss.fillCurrent,
                    dibsCss.sassyFontBodySmallHeavy,
                    dibsCss.mbXxsmall
                )}
            >
                <Shipping variation="20" />
                {title}
            </div>
            {children}
        </div>
    );
};
